/**
 * Transition Rules: EnergyPlus 24.1 to 24.2
 *
 * Based on the official EnergyPlus transition rules from CreateNewIDFUsingRulesV24_2_0.f90
 */

import type { VersionTransition, ObjectTransitionRule } from '../types';
import { createCopyMappings, createShiftedCopyMappings } from '../types';

/**
 * Create the transition definition for 24.1 to 24.2
 */
export function createTransition_24_1_to_24_2(): VersionTransition {
  const objectRules = new Map<string, ObjectTransitionRule>();
  const variableMappings = new Map<string, string>();
  const deletedObjects = new Set<string>();
  const renamedObjects = new Map<string, string>();

  // ============================================================================
  // Object Transition Rules
  // ============================================================================

  /**
   * HeatPump:PlantLoop:EIR:Cooling
   * Insert 3 new fields:
   *   - Field 6: Heat Recovery Inlet Node Name (blank)
   *   - Field 7: Heat Recovery Outlet Node Name (blank)
   *   - Field 11: Heat Recovery Reference Flow Rate (blank)
   *
   * Original: 0-5, 6-8, 9+
   * New:      0-5, 6(new), 7(new), 8-10(was 6-8), 11(new), 12+(was 9+)
   */
  objectRules.set('HEATPUMP:PLANTLOOP:EIR:COOLING', {
    sourceClass: 'HeatPump:PlantLoop:EIR:Cooling',
    targetClass: 'HeatPump:PlantLoop:EIR:Cooling',
    fieldMappings: [
      // Copy fields 0-5 as-is
      ...createCopyMappings(0, 6),
      // Insert Heat Recovery Inlet Node Name at 6
      { sourceIndex: -1, targetIndex: 6, type: 'add_default', defaultValue: '' },
      // Insert Heat Recovery Outlet Node Name at 7
      { sourceIndex: -1, targetIndex: 7, type: 'add_default', defaultValue: '' },
      // Original fields 6-8 → 8-10
      { sourceIndex: 6, targetIndex: 8, type: 'copy' },
      { sourceIndex: 7, targetIndex: 9, type: 'copy' },
      { sourceIndex: 8, targetIndex: 10, type: 'copy' },
      // Insert Heat Recovery Reference Flow Rate at 11
      { sourceIndex: -1, targetIndex: 11, type: 'add_default', defaultValue: '' },
      // Original fields 9+ → 12+
      ...createShiftedCopyMappings(9, 12, 40),
    ],
  });

  /**
   * HeatPump:PlantLoop:EIR:Heating
   * Same pattern as Cooling variant
   */
  objectRules.set('HEATPUMP:PLANTLOOP:EIR:HEATING', {
    sourceClass: 'HeatPump:PlantLoop:EIR:Heating',
    targetClass: 'HeatPump:PlantLoop:EIR:Heating',
    fieldMappings: [
      // Copy fields 0-5 as-is
      ...createCopyMappings(0, 6),
      // Insert Heat Recovery Inlet Node Name at 6
      { sourceIndex: -1, targetIndex: 6, type: 'add_default', defaultValue: '' },
      // Insert Heat Recovery Outlet Node Name at 7
      { sourceIndex: -1, targetIndex: 7, type: 'add_default', defaultValue: '' },
      // Original fields 6-8 → 8-10
      { sourceIndex: 6, targetIndex: 8, type: 'copy' },
      { sourceIndex: 7, targetIndex: 9, type: 'copy' },
      { sourceIndex: 8, targetIndex: 10, type: 'copy' },
      // Insert Heat Recovery Reference Flow Rate at 11
      { sourceIndex: -1, targetIndex: 11, type: 'add_default', defaultValue: '' },
      // Original fields 9+ → 12+
      ...createShiftedCopyMappings(9, 12, 40),
    ],
  });

  return {
    fromVersion: { major: 24, minor: 1 },
    toVersion: { major: 24, minor: 2 },
    objectRules,
    variableMappings,
    deletedObjects,
    renamedObjects,
  };
}
